import {
  EKeyboardKey,
} from '@qmulus/ui/constants/keyboard.ts';
import {
  EKeyboardArea,
} from '@qmulus/ui/utils/keyboard-area.ts';
import {
  isEditableElement,
  isKeyboardEventInArea,
  isNothingFocused,
} from '@qmulus/ui/utils/keyboard-target.ts';
import {
  onBeforeUnmount,
  onMounted,
} from 'vue';
import type {
  ShallowRef,
} from 'vue';

type TArrowKey = EKeyboardKey.ArrowUp | EKeyboardKey.ArrowDown | EKeyboardKey.ArrowLeft | EKeyboardKey.ArrowRight;

const ARROW_KEY_LIST: string[] = [
  EKeyboardKey.ArrowUp,
  EKeyboardKey.ArrowDown,
  EKeyboardKey.ArrowLeft,
  EKeyboardKey.ArrowRight,
];

// Borders of neighboring elements may overlap by a pixel.
const OVERLAP_TOLERANCE_IN_PIXELS = 1;

function isArrowKey(key: string): key is TArrowKey {
  return ARROW_KEY_LIST.includes(key);
}

function getGapBetweenRanges(firstStart: number, firstEnd: number, secondStart: number, secondEnd: number): number {
  return Math.max(0, Math.max(firstStart, secondStart) - Math.min(firstEnd, secondEnd));
}

/**
 * How far the candidate lies from the focused element in the direction of the key, or `undefined`
 * when it does not lie in that direction. Elements in the same column (for up and down) or row (for
 * left and right) come before all others, the nearest of them first.
 */
function getDistanceInDirection({
  focusedRectangle,
  candidateRectangle,
  arrowKey,
}: {
  focusedRectangle: DOMRect,
  candidateRectangle: DOMRect,
  arrowKey: TArrowKey,
}): number | undefined {
  const distanceAlongKey = {
    [EKeyboardKey.ArrowDown]: candidateRectangle.top - focusedRectangle.bottom,
    [EKeyboardKey.ArrowUp]: focusedRectangle.top - candidateRectangle.bottom,
    [EKeyboardKey.ArrowRight]: candidateRectangle.left - focusedRectangle.right,
    [EKeyboardKey.ArrowLeft]: focusedRectangle.left - candidateRectangle.right,
  }[arrowKey];
  if (distanceAlongKey < -OVERLAP_TOLERANCE_IN_PIXELS) {
    return undefined;
  }
  const isVerticalKey = arrowKey === EKeyboardKey.ArrowDown || arrowKey === EKeyboardKey.ArrowUp;
  const distanceAcrossKey = isVerticalKey
    ? getGapBetweenRanges(focusedRectangle.left, focusedRectangle.right, candidateRectangle.left, candidateRectangle.right)
    : getGapBetweenRanges(focusedRectangle.top, focusedRectangle.bottom, candidateRectangle.top, candidateRectangle.bottom);
  const outOfLinePenalty = distanceAcrossKey > 0
    ? Number.MAX_SAFE_INTEGER / 2
    : 0;
  return outOfLinePenalty + distanceAcrossKey + Math.max(0, distanceAlongKey);
}

/**
 * Moves the focus with the arrow keys to the nearest visible element on screen in that direction.
 * With `doesStartWithoutFocus`, an arrow key focuses the first element that is not a text field while
 * nothing on the page has the focus and the keyboard is switched to the content, e.g. after clicking
 * the page or Alt/⌥ + 3.
 */
export function useArrowKeyFocus({
  containerElement,
  focusableSelector,
  doesStartWithoutFocus = false,
}: {
  containerElement: Readonly<ShallowRef<HTMLElement | null>>,
  focusableSelector: string,
  doesStartWithoutFocus?: boolean,
}) {
  function focusFirstElementOnArrowKey(event: KeyboardEvent) {
    const hasModifier = event.altKey || event.ctrlKey || event.metaKey || event.shiftKey;
    if (!isArrowKey(event.key) || hasModifier || event.defaultPrevented || !(event.target instanceof Element)) {
      return;
    }
    if (!isNothingFocused(event.target) || !isKeyboardEventInArea(event, EKeyboardArea.PageContent)) {
      return;
    }
    // Not a text field, as the keys pressed next would type into it.
    const firstElement = Array.from(containerElement.value?.querySelectorAll<HTMLElement>(focusableSelector) ?? [])
      .find((element) => element.checkVisibility() && !isEditableElement(element));
    if (!firstElement) {
      return;
    }
    event.preventDefault();
    firstElement.focus();
  }

  // On the window, so that the keyboard navigation of tables, which listens on the document, comes first.
  if (doesStartWithoutFocus) {
    onMounted(() => {
      window.addEventListener('keydown', focusFirstElementOnArrowKey);
    });
    onBeforeUnmount(() => {
      window.removeEventListener('keydown', focusFirstElementOnArrowKey);
    });
  }

  function moveFocusOnArrowKey(event: KeyboardEvent) {
    if (!isArrowKey(event.key) || !containerElement.value || !(document.activeElement instanceof HTMLElement)) {
      return;
    }
    const isHorizontalKey = event.key === EKeyboardKey.ArrowLeft || event.key === EKeyboardKey.ArrowRight;
    if (isHorizontalKey && document.activeElement instanceof HTMLInputElement) {
      return;
    }
    const focusedRectangle = document.activeElement.getBoundingClientRect();
    // Elements in collapsed sections are still in the DOM, but cannot take the focus.
    const candidateList = Array.from(containerElement.value.querySelectorAll<HTMLElement>(focusableSelector))
      .filter((element) => element !== document.activeElement && element.checkVisibility());

    let nearestElement: HTMLElement | undefined;
    let nearestDistance = Number.POSITIVE_INFINITY;
    for (const candidate of candidateList) {
      const distance = getDistanceInDirection({
        focusedRectangle,
        candidateRectangle: candidate.getBoundingClientRect(),
        arrowKey: event.key,
      });
      if (distance !== undefined && distance < nearestDistance) {
        nearestElement = candidate;
        nearestDistance = distance;
      }
    }
    if (!nearestElement) {
      return;
    }
    event.preventDefault();
    nearestElement.focus();
  }

  return {
    moveFocusOnArrowKey,
  };
}
